import { client } from "../../hooks/Client";
import { useFirestoreOrderBy } from "../../firebase/useFirestore";
import { useState, useContext } from "react";
import { SavedIcon } from "../../StateManagment/SavedIcon";
import { Auth } from "../../StateManagment/Auth";
import { db, timestamp } from "../../firebase/config";
import uuid from "react-uuid";
import Table from "../common/table/Table.jsx";
import Tooltip from "../common/Tooltip";
import DeleteModal from "../common/DeleteModal";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import SelectMechanismActivities from "../mechanisms/SelectMechanismActivities.jsx";
import SelectMechanismEffects from "../mechanisms/SelectMechanismEffects.jsx";
import ReadOnlyGuard from "../common/ReadOnlyGuard";
import AddMechanismsModal from "../mechanisms/AddMechanismsModal";
import SectorTag from "../mechanisms/SectorTag";
import useSettings from "../../hooks/Settings";
import buildMechanismDocs from "../../helpers/impactStrategy/buildMechanismDocs";
import {
  mechanismCategoryLabels,
  getMechanismSignals,
  mechanismCategoriesForSector,
} from "../../helpers/dataHelpers/mechanismCategories";

// ── RECONCILIATION (2026-06-22, resolved 2026-07-02) ──────────────────────────
// `TocMechanisms` (per-tenant, categorized) is THE mechanisms model. The AI's
// storytelling-interview pipeline (functions/mechanisms/saveMechanisms.js)
// writes into this same collection since 2026-07-02 — per-tenant dedup by
// normalized Title, interview provenance in SourceInterviewIDs/Sources. The
// legacy cross-tenant `Mechanisms` + `MechanismLinks` pair is retired (never
// held production data).
// Full details: docs/features/theory-of-change-navigator.md → "Reconciliation".

/**
 * Reusable CRUD body for Werkzame mechanismen (the "why does it work" step).
 * Rendered both on the dedicated TocMechanisms route and inline in the TOC
 * navigator (F7). Owns its own delete modal; the route wrapper keeps the page
 * chrome (PageHeader, Prev/Next). Deletes are soft (ADR-008).
 */
const MechanismManager = () => {
  // Context
  const [, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);
  // Authoring surface, so it narrows to the tenant's sector (ADR-031). An empty
  // SelectedSectors returns every category — never an empty dropdown.
  const selectedSectors = useSettings().selectedSectors;
  const sectorCategories = mechanismCategoriesForSector(selectedSectors);

  // State
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(/** @type {object | null} */ (null));

  // Firestore — filter out soft-deleted mechanisms client-side
  const allMechanisms = useFirestoreOrderBy("TocMechanisms", "Position", "asc");
  const mechanisms = (allMechanisms || []).filter((m) => !m.Deleted);

  // Update the title
  const titleHandler = (e) => {
    db.collection("TocMechanisms")
      .doc(e.target.dataset.docid)
      .update({ Title: e.target.value })
      .then(() => setSaved("flex"));
  };

  // Update the description
  const descriptionHandler = (e) => {
    db.collection("TocMechanisms")
      .doc(e.target.dataset.docid)
      .update({ Description: e.target.value })
      .then(() => setSaved("flex"));
  };

  // Update the category
  const categoryHandler = (e) => {
    db.collection("TocMechanisms")
      .doc(e.target.dataset.docid)
      .update({ Category: e.target.value })
      .then(() => setSaved("flex"));
  };

  // Add every mechanism the user ticked in the picker, in one batch. Shares the
  // payload shape with the navigator's picker via buildMechanismDocs — the two
  // hand-written copies had already drifted (this one dropped `docid`).
  // No activityId: this page has no chain context, so the mechanisms start
  // unlinked and the user connects them in the navigator.
  const addMechanisms = (choices) => {
    const payloads = buildMechanismDocs({
      choices,
      activityId: null,
      startPosition: mechanisms.length + 1,
      client,
      timestamp,
    });
    const batch = db.batch();
    payloads.forEach((payload) => {
      const ref = db.collection("TocMechanisms").doc();
      batch.set(ref, { ...payload, docid: ref.id, ID: uuid() });
    });
    batch.commit().then(() => setSaved("flex"));
  };

  // Open the delete confirmation
  const openDeleteModal = (mechanism) => {
    setDeleteTarget(mechanism);
    setDeleteModal(true);
  };

  // Soft delete: flag the doc, keep the data (ADR-008)
  const deleteMechanism = () => {
    if (!deleteTarget) return;
    db.collection("TocMechanisms")
      .doc(deleteTarget.docid)
      .update({
        Deleted: true,
        DeletedAt: timestamp,
        DeletedBy: auth?.ID || "",
      })
      .then(() => setSaved("flex"));
  };

  const tableColumns = [
    { key: "category", label: "CATEGORIE", searchable: false },
    { key: "sector", label: "SECTOR", searchable: false },
    { key: "title", label: "MECHANISME" },
    { key: "description", label: "BESCHRIJVING", searchable: false },
    { key: "activities", label: "ACTIVITEIT(EN)", searchable: false },
    { key: "effects", label: "EFFECT(EN)", searchable: false },
    { key: "delete", label: "VERWIJDEREN", searchable: false },
  ];

  return (
    <>
      <ReadOnlyGuard edit="strategy.edit">
        {(canEdit) => (
      <Table
        columns={tableColumns}
        items={mechanisms}
        getItemKey={(mechanism) => mechanism.ID}
        renderRow={(mechanism) => {
          // Concrete mechanisms are picked from the chosen category's signal
          // vocabulary (mechanismSignalsByCategory) — no free typing — so they
          // stay comparable across tenants (benchmark). A legacy/free-text Title
          // that predates this is still shown as a fallback option, not dropped.
          const signals = getMechanismSignals(mechanism.Category);
          const title = mechanism.Title || "";
          const hasLegacyTitle = title !== "" && !signals.includes(title);
          // Same escape hatch as the Title above: a Category stored before this
          // tenant's sector narrowed the list would otherwise render a blank
          // select, and one stray change event would wipe it.
          const category = mechanism.Category || "";
          const hasOutOfSectorCategory =
            category !== "" && !sectorCategories.includes(category);
          return (
          <>
            <td>
              <select
                data-docid={mechanism.docid}
                value={category}
                onChange={categoryHandler}
              >
                <option value="">— Kies categorie —</option>
                {hasOutOfSectorCategory && (
                  <option value={category}>
                    {mechanismCategoryLabels[category] || category}
                  </option>
                )}
                {sectorCategories.map((key) => (
                  <option key={key} value={key}>
                    {mechanismCategoryLabels[key]}
                  </option>
                ))}
              </select>
            </td>
            <td className="mechanism-sector-cell">
              <SectorTag category={category} />
            </td>
            <td>
              <select
                data-docid={mechanism.docid}
                value={title}
                onChange={titleHandler}
                disabled={!mechanism.Category}
              >
                <option value="">— Kies mechanisme —</option>
                {hasLegacyTitle && <option value={title}>{title}</option>}
                {signals.map((signal) => (
                  <option key={signal} value={signal}>
                    {signal}
                  </option>
                ))}
              </select>
            </td>
            <td className="common-table__cell--description">
              <textarea
                className="common-table__description-textarea"
                data-docid={mechanism.docid}
                defaultValue={mechanism.Description || ""}
                placeholder="Beschrijving"
                onChange={descriptionHandler}
              />
            </td>
            <td>
              <SelectMechanismActivities mechanism={mechanism} />
            </td>
            <td>
              <SelectMechanismEffects mechanism={mechanism} />
            </td>
            <td>
              {canEdit && (
                <div className="table-delete-container">
                  <Tooltip content={"Mechanisme verwijderen"} top="-80px" left="-50px">
                    <DeleteOutlinedIcon
                      className="table-delete-icon"
                      onClick={() => openDeleteModal(mechanism)}
                    />
                  </Tooltip>
                </div>
              )}
            </td>
          </>
          );
        }}
        searchAccessor={(mechanism) => mechanism?.Title || ""}
        searchPlaceholder="Zoeken op mechanisme"
        emptyMessage="Nog geen werkzame mechanismen toegevoegd."
        addRow={canEdit ? {
          toolTipContent: "Mechanisme toevoegen",
          onClick: () => setAddModal(true),
        } : undefined}
      />
        )}
      </ReadOnlyGuard>

      {addModal && (
      <AddMechanismsModal
        modalOpen={addModal}
        setModalOpen={setAddModal}
        existingTitles={mechanisms.map((m) => m.Title || "")}
        onAdd={addMechanisms}
              />
      )}

      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        deleteItem={deleteMechanism}
        deleteName={deleteTarget?.Title || "dit mechanisme"}
        softDelete
      />
    </>
  );
};

export default MechanismManager;
